import { PieChartSeries } from "~/modules/visualization/components/PieChart";
import { Asset, Token } from "~/modules/assets/types";
import { hexToRGB } from "~/utils/hexToRGB";
import { getRandomColor } from "~/utils/getRandomColor";
import { RWA_COIN_ICON } from "~/consts";
import { uniqueValues } from "~/utils/functions/uniqueValues";
import { AssetPieChartGroupBy } from "./types";

// // // //

// TODO - combine this with the Measures enum in getMeasureValue
export type Measure =
    | "market_value_dollar"
    | "total_asset_value_dollar"
    | "maximum_capacity_dollar"
    | "net_asset_value_dollar"
    | "available_capacity_dollar"
    | "circulating_market_value_dollar"
    | "circulating_asset_value_dollar"
    | "bridged_token_value_dollar"
    | "bridged_token_market_cap_dollar"
    | "outstanding_capital_dollar";

export type AssetPieChartSeries = PieChartSeries & { asset_ids: number[] };

type TokenData = Token & { parentAsset: Asset };

export function getAssetPieChartSeries(props: {
    assets: Asset[];
    groupBy: AssetPieChartGroupBy;
    measure: Measure;
    val30d?: boolean;
}): AssetPieChartSeries[] {
    const { assets, groupBy, measure, val30d = false } = props;

    const COLOR_OPACITY = 0.6;

    // Collect all tokens
    const allTokens: TokenData[] = assets.reduce((all: TokenData[], asset) => {
        return [
            ...all,
            ...asset.tokens.map((t) => {
                return {
                    ...t,
                    parentAsset: asset,
                };
            }),
        ];
    }, []);

    // Build PieChartSeries[] for PieChart
    const series: AssetPieChartSeries[] = allTokens.reduce(
        (allGroups: Array<AssetPieChartSeries>, t) => {
            const matchedGroup = allGroups.find((n) => {
                if (groupBy === AssetPieChartGroupBy.protocol) {
                    return n.id === t.protocol?.slug;
                } else if (groupBy === AssetPieChartGroupBy.issuer) {
                    return n.id === t.parentAsset.issuer_id;
                } else if (groupBy === AssetPieChartGroupBy.asset) {
                    return n.id === t.parentAsset.slug;
                } else if (groupBy === AssetPieChartGroupBy.token) {
                    return n.id === t.id;
                } else if (groupBy === AssetPieChartGroupBy.assetClass) {
                    return n.id === t.parentAsset.asset_class_id;
                } else if (groupBy === AssetPieChartGroupBy.peggedAsset) {
                    return n.id === t.parentAsset.pegged_asset_id;
                } else if (
                    groupBy === AssetPieChartGroupBy.jurisdictionCountry
                ) {
                    return n.id === t.parentAsset.jurisdiction_country_id;
                } else if (groupBy === AssetPieChartGroupBy.manager) {
                    return n.id === t.parentAsset.manager_id;
                } else if (groupBy === AssetPieChartGroupBy.realEstateCountry) {
                    return (
                        n.id === t.parentAsset.real_estate_property?.country_id
                    );
                }

                return n.id === t.network.slug;
            });

            if (!matchedGroup) {
                // Get token value (current or 30d)
                const value = !t[measure]
                    ? 0
                    : val30d
                      ? t[measure]?.val_30d
                      : t[measure]?.val;

                const newGroup: AssetPieChartSeries = {
                    value: value || 0,
                    id: "",
                    name: "",
                    slug: null,
                    color: "",
                    asset_ids: [t.parentAsset.id],
                };

                if (groupBy === AssetPieChartGroupBy.protocol) {
                    newGroup.id = t.protocol?.slug || "N/A";
                    newGroup.name = t.protocol?.name || "N/A";
                    newGroup.icon_url = t.protocol?.icon_url || RWA_COIN_ICON;
                    newGroup.slug = t.protocol?.slug || null;
                    newGroup.color = hexToRGB(
                        t.protocol?.color_hex || getRandomColor(),
                        COLOR_OPACITY,
                    );
                } else if (groupBy === AssetPieChartGroupBy.issuer) {
                    if (!t.parentAsset.issuer_id || !t.parentAsset.issuer) {
                        return allGroups;
                    }
                    newGroup.id = t.parentAsset.issuer_id;
                    newGroup.name = t.parentAsset.issuer.name;
                    newGroup.slug = t.parentAsset.issuer.slug;
                    newGroup.icon_url =
                        t.parentAsset.issuer.icon_url || RWA_COIN_ICON;
                    newGroup.color = hexToRGB(
                        t.parentAsset.issuer.color_hex || getRandomColor(),
                        COLOR_OPACITY,
                    );
                } else if (groupBy === AssetPieChartGroupBy.asset) {
                    newGroup.id = t.parentAsset.slug;
                    newGroup.name = t.parentAsset.name;
                    newGroup.slug = t.parentAsset.slug;
                    newGroup.color = hexToRGB(
                        t.parentAsset.color_hex || getRandomColor(),
                        COLOR_OPACITY,
                    );
                } else if (groupBy === AssetPieChartGroupBy.token) {
                    newGroup.id = t.id;
                    newGroup.name = t.name;
                    newGroup.slug = null;
                    newGroup.color = hexToRGB(
                        t.network.color_hex || getRandomColor(),
                        COLOR_OPACITY,
                    );
                } else if (groupBy === AssetPieChartGroupBy.assetClass) {
                    if (
                        !t.parentAsset.asset_class_id ||
                        !t.parentAsset.asset_class
                    ) {
                        return allGroups;
                    }
                    // newGroup.id = t.parentAsset.asset_class_name || "N/A";
                    newGroup.id = t.parentAsset.asset_class_id || "N/A";
                    newGroup.name = t.parentAsset.asset_class_name || "N/A";
                    newGroup.slug = t.parentAsset.asset_class.slug || null;
                    newGroup.color = hexToRGB(
                        t.parentAsset.asset_class?.color_hex ||
                            getRandomColor(),
                        COLOR_OPACITY,
                    );
                } else if (groupBy === AssetPieChartGroupBy.peggedAsset) {
                    if (
                        !t.parentAsset.pegged_asset_id ||
                        !t.parentAsset.pegged_asset
                    ) {
                        return allGroups;
                    }
                    newGroup.id = t.parentAsset.pegged_asset?.id || "N/A";
                    newGroup.name = t.parentAsset.pegged_asset?.name || "N/A";
                    newGroup.icon_url =
                        t.parentAsset.pegged_asset?.icon_url || RWA_COIN_ICON;
                    newGroup.slug =
                        t.parentAsset.pegged_asset?.ticker ??
                        t.parentAsset.pegged_asset?.slug ??
                        null;
                    newGroup.color = hexToRGB(
                        t.parentAsset.pegged_asset?.color_hex ||
                            getRandomColor(),
                        COLOR_OPACITY,
                    );
                } else if (groupBy === AssetPieChartGroupBy.manager) {
                    // TODO - clean this up once the manager_id=10 is populated
                    if (!t.parentAsset.manager_id || !t.parentAsset.manager) {
                        newGroup.id = t.parentAsset.manager_id || "NO_ID";
                        newGroup.name = "None";
                    } else {
                        newGroup.id = t.parentAsset.manager_id;
                        newGroup.name =
                            t.parentAsset.manager?.name ||
                            `ID: ${t.parentAsset.manager_id}`;
                    }

                    newGroup.color = hexToRGB(
                        t.parentAsset.manager?.color_hex || getRandomColor(),
                        COLOR_OPACITY,
                    );
                    newGroup.icon_url =
                        t.parentAsset.manager?.icon_url || RWA_COIN_ICON;
                } else if (
                    groupBy === AssetPieChartGroupBy.jurisdictionCountry
                ) {
                    if (
                        !t.parentAsset.jurisdiction_country_id ||
                        !t.parentAsset.jurisdiction_country
                    ) {
                        return allGroups;
                    }

                    newGroup.id = t.parentAsset.jurisdiction_country_id;

                    newGroup.name =
                        t.parentAsset.jurisdiction_country_name ||
                        "Not Materially Regulated";

                    newGroup.slug =
                        t.parentAsset.jurisdiction_country?.slug || null;

                    newGroup.icon_url =
                        t.parentAsset.jurisdiction_country?.icon_url ||
                        RWA_COIN_ICON;

                    newGroup.color = hexToRGB(
                        t.parentAsset.jurisdiction_country?.color_hex ||
                            getRandomColor(),
                        COLOR_OPACITY,
                    );
                } else if (groupBy === AssetPieChartGroupBy.realEstateCountry) {
                    if (
                        !t.parentAsset.real_estate_property?.country_id ||
                        !t.parentAsset.real_estate_property?.country
                    ) {
                        return allGroups;
                    }

                    newGroup.id =
                        t.parentAsset.real_estate_property?.country_id;

                    newGroup.name =
                        t.parentAsset.real_estate_property?.country?.name ||
                        "N/A";

                    newGroup.slug =
                        t.parentAsset.real_estate_property?.country?.slug ||
                        null;

                    newGroup.icon_url =
                        t.parentAsset.real_estate_property?.country?.icon_url ||
                        RWA_COIN_ICON;

                    newGroup.color = hexToRGB(
                        t.parentAsset.real_estate_property?.country
                            ?.color_hex || getRandomColor(),
                        COLOR_OPACITY,
                    );
                } else {
                    newGroup.id = t.network.slug;
                    newGroup.name = t.network.name;
                    newGroup.slug = t.network.slug;
                    newGroup.icon_url = t.network.icon_url;
                    newGroup.color = hexToRGB(
                        t.network.color_hex || getRandomColor(),
                        COLOR_OPACITY,
                    );
                }

                return [...allGroups, newGroup];
            }

            // Accumulate token value
            const tokenValue = !t[measure]
                ? 0
                : val30d
                  ? t[measure]?.val_30d
                  : t[measure]?.val;

            matchedGroup.value = matchedGroup.value + (tokenValue || 0);

            // Accumulate unique asset IDs
            matchedGroup.asset_ids = uniqueValues([
                ...matchedGroup.asset_ids,
                t.parentAsset.id,
            ]);

            return [
                ...allGroups.map((each) => {
                    if (each.id === matchedGroup.id) {
                        return matchedGroup;
                    }
                    return each;
                }),
            ];
        },
        [],
    );

    return series;
}
